'use client'

import { ExportWarningsModalHost } from '@/components/price-sync/export-warnings-modal'
import { mutationRetry } from '@/api'
import { registerAgGridModules } from '@/components/AGGrid/register-modules'
import { ToastProvider } from '@/components/toast'
import { AuthContextProvider } from '@/providers/auth-context'
import { generateColors } from '@mantine/colors-generator'
import {
  Button,
  createTheme,
  InputWrapper,
  MantineColorsTuple,
  MantineProvider
} from '@mantine/core'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { LicenseManager } from 'ag-grid-enterprise'
import { SessionProvider } from 'next-auth/react'
import NextTopLoader from 'nextjs-toploader'
import React, { useState } from 'react'

// Must run at module load, before any grid renders.
registerAgGridModules()

const sharedComponents = {
  InputWrapper: InputWrapper.extend({
    styles: {
      label: { marginBottom: 2 }
    }
  }),
  // Brand: callouts / buttons use Inconsolata, uppercase (per the brand guide).
  Button: Button.extend({
    styles: {
      root: {
        fontFamily: 'var(--font-inconsolata), monospace',
        textTransform: 'uppercase',
        letterSpacing: '0.03em'
      }
    }
  })
}

// Brand typography (Celerity Brand Guide): Reckless for headings (Fraunces stands in as a
// free display serif until Reckless is self-hosted), Neue Haas Grotesk for body/subheads
// (Open Sans fallback). Inconsolata for buttons/callouts is applied via the Button override.
const brandTypography = {
  fontFamily:
    '"Neue Haas Grotesk Text Pro", var(--font-body), system-ui, sans-serif',
  headings: {
    fontFamily: '"Reckless", var(--font-heading), Georgia, serif'
  }
}

// Brand accent palette. Each brand hex is expanded to a full Mantine 10-shade scale so
// hover/active states derive correctly; exposed as Mantine colors + CSS variables
// (e.g. var(--mantine-color-brandLime-6)).
const brandColors = {
  brandNavy: generateColors('#182E35'), // Primary — deep, rich backgrounds
  brandLightBlue: generateColors('#A3CBD9'), // Secondary — contrast / balance
  brandLime: generateColors('#CFDB27'), // Accent — small doses, gradient blends
  brandMint: generateColors('#81CBB2'), // Supplemental accent
  brandGreen: generateColors('#1CAC7F'), // Supplemental accent
  brandGold: generateColors('#EFCE47'), // Supplemental accent
  brandCream: generateColors('#FAF6F2') // Light neutral
}

// Primary scale: brand accent blue (#3278BB) at index 5 — the shade the app references
// throughout — ramping to the brand navy (#182E35) at the dark end.
const celerityBrandProd: MantineColorsTuple = [
  '#eaf3fb',
  '#d4e4f4',
  '#b8d3ec',
  '#8fbbe1',
  '#5f9bce',
  '#3278bb',
  '#2c6aa8',
  '#255687',
  '#1f4266',
  '#182e35'
]

// Single brand theme for all environments. (The old green non-prod theme was removed —
// environment is now surfaced with an explicit badge in the navbar instead of recoloring
// the whole app, which conflicted with brand theming.)
const brandTheme = createTheme({
  ...brandTypography,
  colors: { ...brandColors, celerityBrand: celerityBrandProd },
  primaryColor: 'celerityBrand',
  primaryShade: 5,
  cursorType: 'pointer',
  // Squarer corners than Mantine's 4px default: 2px reads as a crisp field edge
  // rather than a pill, which suits a dense data application and matches the
  // brand's flat, geometric treatment. Applies to every component that derives
  // from the default radius — inputs, buttons, cards, modals — so the app stays
  // internally consistent.
  defaultRadius: 'xs',
  components: sharedComponents
})

// Sentry release/environment are build-time inlined env vars. Read and log them once
// at module scope — doing this in the component body logged twice because React
// StrictMode double-renders components in development.
const sentryRelease = process.env.NEXT_PUBLIC_SENTRY_RELEASE || 'local'
const sentryEnvironment = process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || 'local'

console.info(`Release: ${sentryRelease}`)
console.info(`Environment: ${sentryEnvironment}`)

export default function Providers({ children }: { children: React.ReactNode }) {
  LicenseManager.setLicenseKey(
    process.env.NEXT_PUBLIC_AG_GRID_LICENSE_KEY || ''
  )

  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: 1 },
          // Writes retry once, except stale-organization-context rejections:
          // retrying those would land the write on the new organization.
          mutations: { retry: mutationRetry }
        }
      })
  )

  const providers = (
    <MantineProvider theme={brandTheme}>
      <QueryClientProvider client={client}>
        <SessionProvider>
          <AuthContextProvider>
            <NextTopLoader color="white" />
            <div className="h-full w-full">{children}</div>
            <ToastProvider />
            <ExportWarningsModalHost />
          </AuthContextProvider>
        </SessionProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </MantineProvider>
  )

  return providers
}
